import React from 'react'

import { Spinner, WebViewActionStrip } from '@acorns/web-components'
import { pathOr } from 'ramda'
import styled from 'styled-components'

import { CenteredContentContainer } from 'pages/spend/direct-deposit/SharedComponents'
import { style } from 'theme'
import { PageAnalytics } from 'utils/analytics'
import { SPINNER } from 'utils/testing/testIDs'

import {
  Container,
  InfoBlock,
  ProgressBarContainer,
  SectionTitle,
} from '../../WebViewComponents'
import { Step, WVProps } from '../SearchComponents'
import enhance from './enhance'
import SearchBox from './SearchBox'
import SearchResultItem, { AddEmployerItem } from './SearchResultItem'

export const StyledContainer = styled(Container)`
  background-color: ${style((theme) => theme.colors.ivoryLight)};
`

export const SearchInfoBlock = styled(InfoBlock)`
  padding: 30px 50px 20px 50px;
`

export const SearchResults = styled.div`
  width: 100%;
`

export const EmptySearch = styled.div`
  color: ${style((theme) => theme.colors.stoneLight)};
  font-size: 22px;
  font-weight: 500;
  line-height: 29px;
  padding: 42px 0;
  text-align: center;
  height: calc(100vh - 196px);
  display: flex;
  align-items: center;
  justify-content: center;
`

export const EmptyResults = styled(EmptySearch)`
  font-size: 14px;
  font-weight: 200;
  line-height: 19px;
  padding: 30px 0;
  text-align: center;
`

export const SearchDivider = styled.div`
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  background-color: ${style((theme) => theme.colors.ivoryLight)};
  color: ${style((theme) => theme.colors.stone)};
  padding: 12px 20px;
  box-shadow: ${style((theme) => theme.shadows.smLight)};
  border-top: 1px solid #efefef;
  border-bottom: 1px solid #efefef;
`

export const TopEmployerSection = styled.div`
  background-color: ${style((theme) => theme.colors.white)};
`
export const EmployerLogo = styled.img`
  width: 106px;
  height: 106px;
  margin: -10px; /* zoom in on the logo */
`

export const EmployerLogoContainer = styled.div`
  width: 86px;
  height: 86px;
  overflow: hidden;
  border-radius: 50%;
  display: flex;
  box-shadow: ${style((theme) => theme.shadows.smMedium)};
`

export const EmployerName = styled.div`
  margin-top: 10px;
  text-align: center;
  font-weight: 400;
  font-size: 14px;
  color: ${style((theme) => theme.colors.slate)};
  height: 22px;
  line-height: 22px;
`

export const TopEmployerList = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
`

export const TopEmployerListItem = styled.li`
  list-style-type: none;
  margin: 15px;
  width: 85px;
`

const Page: React.SFC<WVProps> = (props: WVProps) => (
  <>
    {props.data.loading && (
      <CenteredContentContainer data-testid={SPINNER}>
        <Spinner />
      </CenteredContentContainer>
    )}

    {!props.data.loading && (
      <StyledContainer>
        <PageAnalytics />
        <WebViewActionStrip
          title="Direct deposit"
          action={{
            onClick: props.handleWebViewExit,
            shouldShowCloseIcon: true,
          }}
          link={{ onClick: props.handleWebViewExit, text: 'Cancel' }}
        />

        <ProgressBarContainer progress={40} />

        <SearchInfoBlock>{props.heroSubtitle}</SearchInfoBlock>

        <SearchBox onSearch={props.handleSearch} />

        <SearchResults>
          {props.step === Step.Results && props.searching && (
            <SearchDivider>Searching...</SearchDivider>
          )}
          {props.step === Step.Results && !props.searching && (
            <SearchDivider>Search Results</SearchDivider>
          )}
          {props.step === Step.Results &&
            props.results &&
            props.results.length > 0 && (
              <>
                {props.results.map((result, index) => (
                  <SearchResultItem
                    key={result.id}
                    employerId={result.id}
                    employerName={result.employerName}
                    employerCount={props.results.length}
                    employerIndex={index}
                    requiresEmployerMailingAddress={pathOr(
                      null,
                      ['switch', 'requiresEmployerMailingAddress'],
                      result,
                    )}
                    ctaUrl={
                      result.switchType === 'online' ? result.switch.url : ''
                    }
                    trackingId={props.trackingId}
                    onClick={props.handleResultClick}
                    logoUrl={result.logoUrl}
                    switchType={result.switchType}
                    clickSwitchTargetId={
                      result.switchType === 'online'
                        ? result.switch.onlineVendorTargetId
                        : result.switch.submittableVendorTargetId
                    }
                  />
                ))}
              </>
            )}

          {props.step === Step.Results && (
            <>
              <SearchDivider>Not Finding Your Employer?</SearchDivider>
              <AddEmployerItem onClick={props.handleAddEmployer} />
            </>
          )}
        </SearchResults>
        {props.showTopEmployers && (
          <TopEmployerSection>
            <SectionTitle>Top employers</SectionTitle>
            <TopEmployerList>
              {props.topEmployersList?.map((employer) => {
                return (
                  <TopEmployerListItem key={employer.employerName}>
                    <EmployerLogoContainer>
                      <EmployerLogo src={employer.logo} alt="" />
                    </EmployerLogoContainer>
                    <EmployerName>{employer.employerName}</EmployerName>
                  </TopEmployerListItem>
                )
              })}
            </TopEmployerList>
          </TopEmployerSection>
        )}
      </StyledContainer>
    )}
  </>
)

export default enhance(Page)
export { enhance, Page }
